import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  disableWebPush,
  enableWebPush,
  getWebPushBrowserStatus,
  getWebPushServerStatus,
  previewWebPushTest,
  sendWebPushTest,
  syncExistingWebPushSubscription,
  type WebPushBrowserStatus,
  type WebPushServerStatus,
} from './webPushClient';

type WebPushSettingsPanelProps = {
  authenticated: boolean;
};

const EMPTY_SERVER_STATUS: WebPushServerStatus = {
  serverConfigured: false,
  vapidPublicKey: '',
  subscribed: false,
  subscriptionCount: 0,
};

const EMPTY_BROWSER_STATUS: WebPushBrowserStatus = {
  supported: false,
  permission: 'unsupported',
  subscribed: false,
};

export default function WebPushSettingsPanel({ authenticated }: WebPushSettingsPanelProps) {
  const [serverStatus, setServerStatus] = useState<WebPushServerStatus>(EMPTY_SERVER_STATUS);
  const [browserStatus, setBrowserStatus] = useState<WebPushBrowserStatus>(EMPTY_BROWSER_STATUS);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    if (!authenticated) return;
    try {
      const [initialServer, browser] = await Promise.all([
        getWebPushServerStatus(),
        getWebPushBrowserStatus(),
      ]);
      let server = initialServer;

      if (server.serverConfigured && browser.subscribed && browser.permission === 'granted') {
        await syncExistingWebPushSubscription();
        // 브라우저 local subscription만 존재하는 false-positive를 막기 위해
        // 서버 DB 동기화 직후 authoritative status를 다시 읽는다.
        server = await getWebPushServerStatus();
      }

      setServerStatus(server);
      setBrowserStatus(browser);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '웹푸시 상태를 확인하지 못했습니다.');
    }
  }, [authenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const pushReady = browserStatus.supported
    && browserStatus.permission === 'granted'
    && browserStatus.subscribed
    && serverStatus.serverConfigured
    && serverStatus.subscribed
    && serverStatus.subscriptionCount > 0;

  const stateLabel = useMemo(() => {
    if (!browserStatus.supported) return '브라우저 미지원';
    if (!serverStatus.serverConfigured) return '서버 설정 준비 중';
    if (browserStatus.permission === 'denied') return '브라우저에서 차단됨';
    if (pushReady) return '웹푸시 사용 중';
    if (browserStatus.subscribed && !serverStatus.subscribed) return '서버 동기화 필요';
    return '웹푸시 꺼짐';
  }, [browserStatus, pushReady, serverStatus.serverConfigured, serverStatus.subscribed]);

  const handleEnable = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      await enableWebPush(serverStatus.vapidPublicKey);
      setMessage('보유종목 변동 웹푸시 알림이 켜졌습니다.');
      await refresh();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '웹푸시를 켜지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [refresh, serverStatus.vapidPublicKey]);

  const handleDisable = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      await disableWebPush();
      setMessage('이 브라우저의 보유종목 변동 웹푸시 알림을 껐습니다.');
      await refresh();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '웹푸시를 끄지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  const handlePreview = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const payload = await previewWebPushTest();
      setMessage(`알림 화면 미리보기 표시 완료: ${payload.title}`);
      setBrowserStatus((current) => ({
        ...current,
        supported: true,
        permission: 'granted',
      }));
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '알림 화면 미리보기에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTest = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const sent = await sendWebPushTest();
      setMessage(sent > 0 ? '테스트 웹푸시를 보냈습니다.' : '발송 가능한 현재 브라우저 구독을 확인하지 못했습니다.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '테스트 웹푸시 발송에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  if (!authenticated) return null;

  const canEnable = browserStatus.supported
    && serverStatus.serverConfigured
    && browserStatus.permission !== 'denied'
    && !browserStatus.subscribed;

  return (
    <section className="live-summary-web-push">
      <header>
        <div>
          <small>브라우저 알림</small>
          <strong>웹푸시</strong>
        </div>
        <b className={pushReady ? 'is-on' : ''}>{stateLabel}</b>
      </header>

      <p>포트폴리오에 등록한 보유종목이 사용자가 설정한 당일 상승·하락 기준에 처음 진입할 때 알려드립니다. 시장 전체·스캐너 변동은 웹푸시하지 않습니다.</p>

      <div className="live-summary-web-push__actions">
        {browserStatus.supported && browserStatus.permission !== 'denied' && (
          <button type="button" onClick={handlePreview} disabled={loading}>
            알림 화면 미리보기
          </button>
        )}
        {browserStatus.subscribed ? (
          <button type="button" onClick={handleDisable} disabled={loading}>웹푸시 끄기</button>
        ) : (
          <button type="button" className="is-primary" onClick={handleEnable} disabled={loading || !canEnable}>웹푸시 켜기</button>
        )}
        {pushReady && (
          <button type="button" onClick={handleTest} disabled={loading}>테스트 알림</button>
        )}
      </div>

      {!serverStatus.serverConfigured && (
        <small className="live-summary-web-push__notice">
          VAPID 서버 설정이 아직 적용되지 않아 실제 구독·전송은 비활성입니다. 알림 화면 미리보기는 서버 전송 없이 확인할 수 있습니다.
        </small>
      )}
      {browserStatus.subscribed && serverStatus.serverConfigured && !serverStatus.subscribed && (
        <small className="live-summary-web-push__notice">
          브라우저 구독은 존재하지만 서버 구독 동기화가 확인되지 않았습니다. 페이지를 다시 열거나 웹푸시를 껐다가 다시 켜주세요.
        </small>
      )}
      {browserStatus.permission === 'denied' && (
        <small className="live-summary-web-push__notice">브라우저 사이트 설정에서 알림 권한을 허용해야 다시 켤 수 있습니다.</small>
      )}
      {message && <small className="live-summary-web-push__message">{message}</small>}
    </section>
  );
}
